import { Box, Button, Flex, Group, Image, Input, Loader, Stack, Text } from '@mantine/core';
import type { FileRejection } from '@mantine/dropzone';
import { Dropzone, IMAGE_MIME_TYPE } from '@mantine/dropzone';
import { CheckCircleIcon, ImageSquareIcon, PencilSimpleIcon, UploadSimpleIcon } from '@phosphor-icons/react';
import { useCallback, useRef, useState } from 'react';
import type { FC } from 'react';

import { env } from '../../../env.ts';

const ACCEPTED_EXTENSIONS = '.png,.gif,.jpeg,.jpg';
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ACCEPTED_MIME_TYPES = IMAGE_MIME_TYPE.filter(
  (type) => type === 'image/png' || type === 'image/jpeg' || type === 'image/gif',
);

interface ImageDropzoneProps {
  croppedImageId: string | null;
  error?: string;
  isLoadingOriginal?: boolean;
  /** Local blob URL for instant preview after crop, avoids flash of old Directus image */
  localPreviewSrc?: string | null;
  /** Only true right after a fresh upload — hides the banner on initial load */
  justUploaded?: boolean;
  onFileDrop: (file: File) => void;
  onEditCrop: () => void;
  onReplace: () => void;
}

export const ImageDropzone: FC<ImageDropzoneProps> = ({
  croppedImageId,
  error,
  isLoadingOriginal,
  localPreviewSrc,
  justUploaded,
  onFileDrop,
  onEditCrop,
  onReplace,
}) => {
  const [rejectError, setRejectError] = useState<string | null>(null);
  const hasCroppedImage = !!croppedImageId;
  const displayError = error ?? rejectError;

  const replaceInputRef = useRef<HTMLInputElement>(null);

  const handleReplaceClick = useCallback(() => {
    replaceInputRef.current?.click();
  }, []);

  const handleReplaceFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      e.target.value = '';
      onReplace();
      onFileDrop(file);
    },
    [onReplace, onFileDrop],
  );

  if (hasCroppedImage) {
    return (
      <Input.Wrapper error={displayError}>
        <input
          accept={ACCEPTED_EXTENSIONS}
          ref={replaceInputRef}
          style={{ display: 'none' }}
          type="file"
          onChange={handleReplaceFileChange}
        />
        <Box
          bd={displayError ? '1px solid var(--mantine-color-red-5)' : '1px solid var(--mantine-color-gray-3)'}
          style={{ borderRadius: 'var(--mantine-radius-sm)', overflow: 'hidden' }}
        >
          <Flex align="center" justify="space-between" pt="sm" px="sm">
            <Text fw={600} size="sm">
              Event artwork
            </Text>
            <Group gap="xs">
              <Button
                leftSection={isLoadingOriginal ? <Loader size={18} /> : <PencilSimpleIcon size={18} weight="bold" />}
                loading={isLoadingOriginal}
                radius="sm"
                size="xs"
                styles={{ section: { marginRight: 8 } }}
                variant="default"
                onClick={onEditCrop}
              >
                Edit crop
              </Button>
              <Button
                leftSection={<UploadSimpleIcon size={18} weight="bold" />}
                radius="sm"
                size="xs"
                styles={{ section: { marginRight: 8 } }}
                variant="default"
                onClick={handleReplaceClick}
              >
                Replace
              </Button>
            </Group>
          </Flex>
          <Box pb="sm" pt="xs" px="sm">
            <Image
              alt="Event artwork"
              crossOrigin="anonymous"
              radius="md"
              src={localPreviewSrc ?? `${env.VITE_DIRECTUS_API_URL}/assets/${croppedImageId}?format=auto`}
              style={{ aspectRatio: '4/5', objectFit: 'cover' }}
              w="100%"
            />
            {justUploaded && (
              <Flex
                align="center"
                bg="var(--mantine-color-green-1)"
                gap={4}
                mt="xs"
                px="sm"
                py={8}
                style={{ borderRadius: 12 }}
              >
                <CheckCircleIcon color="var(--mantine-color-green-7)" size={16} weight="fill" />
                <Text c="green.8" fw={500} size="xs">
                  Uploaded successfully
                </Text>
              </Flex>
            )}
          </Box>
        </Box>
      </Input.Wrapper>
    );
  }

  const handleReject = (rejections: FileRejection[]) => {
    const firstRejection = rejections[0];
    if (!firstRejection) return;

    const errorCode = firstRejection.errors[0]?.code;
    if (errorCode === 'file-too-large') {
      setRejectError('File is too large. Maximum size is 10MB');
    } else if (errorCode === 'file-invalid-type') {
      setRejectError('Please upload a JPG, PNG or GIF image');
    } else {
      setRejectError('Invalid file');
    }
  };

  const handleDrop = (files: File[]) => {
    setRejectError(null);
    if (files[0]) onFileDrop(files[0]);
  };

  return (
    <Input.Wrapper error={displayError}>
      <Dropzone
        accept={ACCEPTED_MIME_TYPES}
        aria-label="Upload event artwork"
        maxFiles={1}
        maxSize={MAX_FILE_SIZE}
        multiple={false}
        radius="sm"
        styles={{
          root: {
            border: displayError ? '2px dashed var(--mantine-color-red-5)' : '2px dashed var(--mantine-color-gray-4)',
            borderRadius: 'var(--mantine-radius-sm)',
          },
        }}
        onDrop={handleDrop}
        onReject={handleReject}
      >
        <Flex align="flex-start" gap="sm" p="sm">
          <Box
            bg="gray.1"
            style={{
              width: 100,
              height: 100,
              borderRadius: 'var(--mantine-radius-sm)',
              border: '1px solid var(--mantine-color-gray-3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <ImageSquareIcon color="var(--mantine-color-gray-5)" size={48} weight="fill" />
          </Box>
          <Stack gap={2} style={{ flex: 1 }}>
            <Dropzone.Idle>
              <Text fw={600} size="sm">
                Add event artwork
              </Text>
              <Text c="dimmed" mt={2} size="xs">
                Drag and drop your image here, or click to browse.
              </Text>
              <Text c="dimmed" mt={2} size="xs">
                JPG, PNG or GIF · Max 10MB · 4:5 portrait format recommended
              </Text>
              <Button color="dark" component="span" mt="xs" radius="xl" size="xs" variant="outline">
                Browse files
              </Button>
            </Dropzone.Idle>
            <Dropzone.Accept>
              <Text c="blue" fw={500} size="sm">
                Drop your image here
              </Text>
            </Dropzone.Accept>
            <Dropzone.Reject>
              <Text c="red" fw={500} size="sm">
                Only JPG, PNG and GIF images (max 10MB)
              </Text>
            </Dropzone.Reject>
          </Stack>
        </Flex>
      </Dropzone>
    </Input.Wrapper>
  );
};
